import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { authContainer } from '@/modules/auth/di/container';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Reconcilia a sessão com o **Firebase** (fonte de verdade do login), não só com
 * o localStorage. Espera o Firebase resolver: se há usuário, reidrata o snapshot
 * persistido (roles/email/accountId) pra evitar flash e, em seguida, revalida
 * as roles contra `GET /auth/me/perks` — sem isso, um perk concedido depois do
 * login só refletiria no próximo logout/login (roles ficavam presas no
 * snapshot da sessão anterior). Se a revalidação falhar (rede/gateway fora),
 * mantém o snapshot local em vez de derrubar a sessão. Se NÃO há usuário
 * Firebase (sessão fantasma — Zustand/localStorage teimando depois que a
 * sessão Firebase sumiu), limpa o `auth` e manda pro /sign-in em vez de fingir
 * logado — o que deixava toda chamada autenticada bater 401 no gateway.
 *
 * TODO: a revalidação usa o `x-scope-id` fixo do app (`VITE_SCOPE_ID`) — quando
 * existirem roles/features globais (não amarradas a um scope), essa busca
 * precisa passar a considerá-las também.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    async function reconcile() {
      if (!useAuthStore.getState().isAuthenticated) {
        const stored = authContainer.repository.getStoredAuth();
        if (stored) setAuth(stored);
      }
      try {
        const refreshed = await authContainer.repository.refreshRoles();
        if (refreshed) setAuth(refreshed);
      } catch {
        // Revalidação falhou (rede/gateway fora): segue com o snapshot local.
      }
      setHasSession(true);
      setReady(true);
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        void reconcile();
      } else {
        void authContainer.repository.clearAuth();
        clearAuth();
        setHasSession(false);
        setReady(true);
      }
    });
    return () => unsubscribe();
  }, [setAuth, clearAuth]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-(--color-accent) h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!hasSession) {
    return <Navigate to="/sign-in" replace />;
  }

  return <>{children}</>;
}
